import React, { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import SlideOver from "@/components/SlideOver";
import UpgradeModal from "@/components/UpgradeModal";
import { useAuth } from "@/context/AuthContext";
import { isBusinessActive } from "@/lib/upgrade";
import DirectorDebugTag from "@/components/DirectorDebugTag";
import {
  Building2, Plus, Upload, Trash2, Users, CalendarClock, Wand2, Coins, HeartHandshake,
  Loader2, Image as ImageIcon, FileText, Check, ShieldCheck, Sparkles, Clock, X, LayoutDashboard, Moon, Lock,
} from "lucide-react";

const parseCSV = (text) => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const header = lines[0].toLowerCase().split(",").map((h) => h.trim());
  const idx = (k) => header.findIndex((h) => h.includes(k));
  const iName = idx("name"), iEmail = idx("email"), iPhone = idx("phone"), iBday = idx("birth"), iStart = idx("start") >= 0 ? idx("start") : idx("anniv");
  const iLine1 = idx("address"), iCity = idx("city"), iState = idx("state"), iZip = idx("zip");
  return lines.slice(1).map((l) => {
    const c = l.split(",");
    return {
      name: (c[iName] || "").trim(), email: (c[iEmail] || "").trim(), phone: iPhone >= 0 ? (c[iPhone] || "").trim() : "",
      birthday: (c[iBday] || "").trim(), start_date: (c[iStart] || "").trim(),
      address_line1: iLine1 >= 0 ? (c[iLine1] || "").trim() : "", address_city: iCity >= 0 ? (c[iCity] || "").trim() : "",
      address_state: iState >= 0 ? (c[iState] || "").trim() : "", address_zip: iZip >= 0 ? (c[iZip] || "").trim() : "",
      address_country: "US",
    };
  }).filter((r) => r.name);
};

const VERDICT_PILL = {
  deliverable: "bg-secondary/15 text-secondary border-secondary/30",
  deliverable_unnecessary_unit: "bg-secondary/15 text-secondary border-secondary/30",
  deliverable_incorrect_unit: "bg-yellow-500/10 gold-text border-yellow-500/20",
  undeliverable: "bg-red-500/10 text-red-400 border-red-500/20",
  no_address: "bg-muted/40 text-muted-foreground border-border",
};

const Captain = () => {
  const { user } = useAuth();
  const businessActive = isBusinessActive(user);
  const [orgs, setOrgs] = useState([]);
  const [active, setActive] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [fund, setFund] = useState(null);
  const [charities, setCharities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [contrib, setContrib] = useState({ name: "", amount: 5 });
  const csvRef = useRef(null);
  const sealRef = useRef(null);
  const [upgradeGate, setUpgradeGate] = useState(false);
  // Business Lane: Address Auditor, Scheduled Sends, Dashboard
  const [auditing, setAuditing] = useState(false);
  const [auditResults, setAuditResults] = useState(null);
  const [stylizing, setStylizing] = useState(false);
  const [scheduled, setScheduled] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [newSend, setNewSend] = useState({ member_id: "", occasion: "Birthday", send_date: "", delivery_method: "physical" });
  const [schedulingBusy, setSchedulingBusy] = useState(false);

  const load = useCallback(async () => {
    const [o, c] = await Promise.all([api.get("/orgs"), api.get("/charities")]);
    setOrgs(o.data); setCharities(c.data.charities); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openOrg = async (id) => {
    const [o, m, f] = await Promise.all([api.get(`/orgs/${id}`), api.get(`/orgs/${id}/milestones`), api.get(`/orgs/${id}/fund`)]);
    setActive(o.data); setMilestones(m.data); setFund(f.data);
    setAuditResults(null);
    if (businessActive) {
      api.get(`/orgs/${id}/scheduled-sends`).then((r) => setScheduled(r.data)).catch(() => {});
      if (!newSend.member_id && o.data.roster?.length) setNewSend((s) => ({ ...s, member_id: o.data.roster[0].id }));
    }
  };

  const createOrg = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { const r = await api.post("/orgs", { name }); setCreateOpen(false); setName(""); await load(); openOrg(r.data.id); }
    finally { setBusy(false); }
  };

  const onCSV = async (e) => {
    const file = e.target.files?.[0]; if (!file || !active) return;
    const text = await file.text();
    const rows = parseCSV(text);
    if (!rows.length) { alert("No rows found. CSV needs a header with name,email,birthday,start_date,address,city,state,zip."); return; }
    await api.post(`/orgs/${active.id}/roster/import`, { rows });
    if (csvRef.current) csvRef.current.value = "";
    openOrg(active.id);
  };

  const auditAddresses = async () => {
    if (!businessActive) { setUpgradeGate(true); return; }
    setAuditing(true); setAuditResults(null);
    try {
      const r = await api.post(`/orgs/${active.id}/roster/audit-addresses`);
      setAuditResults(r.data);
    } catch (err) {
      if (err?.response?.status === 403) setUpgradeGate(true);
      else alert(err?.response?.data?.detail || "Address audit failed.");
    } finally { setAuditing(false); }
  };

  const onSeal = async (e) => {
    const file = e.target.files?.[0]; if (!file || !active) return;
    if (!businessActive) { setUpgradeGate(true); if (sealRef.current) sealRef.current.value = ""; return; }
    const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(file); });
    try {
      await api.post(`/orgs/${active.id}/seal`, { image_base64: b64 });
      openOrg(active.id);
      alert("Corporate seal set — it will replace the Gold Seal on this org's mailed cards.");
    } catch (err) {
      if (err?.response?.status === 403) setUpgradeGate(true);
      else alert(err?.response?.data?.detail || "Seal upload failed.");
    } finally { if (sealRef.current) sealRef.current.value = ""; }
  };

  const stylizeSeal = async () => {
    if (!businessActive) { setUpgradeGate(true); return; }
    if (!active?.corporate_seal_base64) { alert("Upload a logo first."); return; }
    setStylizing(true);
    try {
      await api.post(`/orgs/${active.id}/seal/stylize`, { image_base64: active.corporate_seal_base64 });
      openOrg(active.id);
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not stylize the logo.");
    } finally { setStylizing(false); }
  };

  const removeMember = async (mid) => { await api.delete(`/orgs/${active.id}/roster/${mid}`); openOrg(active.id); };

  const autoCurate = async () => {
    setBusy(true);
    try { const r = await api.post(`/orgs/${active.id}/auto-curate`); alert(`Curated ${r.data.created} team card(s), ${r.data.skipped} already existed. See Curations.`); }
    finally { setBusy(false); }
  };

  const createScheduledSend = async () => {
    if (!businessActive) { setUpgradeGate(true); return; }
    if (!newSend.member_id || !newSend.send_date) { alert("Choose a team member and a date."); return; }
    setSchedulingBusy(true);
    try {
      const r = await api.post(`/orgs/${active.id}/scheduled-sends`, newSend);
      setScheduled((s) => [...s, r.data].sort((a, b) => a.send_date.localeCompare(b.send_date)));
    } catch (err) {
      if (err?.response?.status === 403) setUpgradeGate(true);
      else alert(err?.response?.data?.detail || "Could not schedule the send.");
    } finally { setSchedulingBusy(false); }
  };

  const deleteScheduledSend = async (id) => {
    await api.delete(`/orgs/${active.id}/scheduled-sends/${id}`);
    setScheduled((s) => s.filter((x) => x.id !== id));
  };

  const openDashboard = async () => {
    if (!businessActive) { setUpgradeGate(true); return; }
    try {
      const r = await api.get(`/orgs/${active.id}/dashboard`);
      setDashboard(r.data);
      setDashboardOpen(true);
    } catch (err) {
      if (err?.response?.status === 403) setUpgradeGate(true);
      else alert(err?.response?.data?.detail || "Could not load the dashboard.");
    }
  };

  const contribute = async () => {
    if (!contrib.name.trim()) { alert("Enter contributor name"); return; }
    await api.post(`/orgs/${active.id}/fund/contribute`, { name: contrib.name, amount: parseFloat(contrib.amount) || 0, recurring: false });
    openOrg(active.id);
  };

  const allocateCharity = async (ein) => {
    const amt = parseFloat(window.prompt("Donate how much from the pot?", "10"));
    if (!amt) return;
    try { await api.post(`/orgs/${active.id}/fund/allocate`, { to: "charity", amount: amt, charity_ein: ein }); openOrg(active.id); }
    catch (e) { alert(e?.response?.data?.detail || "Allocation failed."); }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl mb-1 flex items-center gap-3">Club Captain <Building2 className="w-7 h-7 text-primary" /></h1>
          <p className="text-muted-foreground">Enterprise & philanthropy — rosters, team milestones, group pots, and impact.</p>
        </div>
        <button data-testid="create-org-btn" onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white rounded-2xl px-5 py-2.5 hover:opacity-90"><Plus className="w-4 h-4" /> New Organization</button>
      </div>

      {!businessActive && (
        <div className="surface rounded-2xl p-4 mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="business-upsell-banner">
          <div className="flex items-center gap-2 text-sm"><ShieldCheck className="w-4 h-4 gold-text" /> Org creation is free. The Business Team Plan ($29/mo) unlocks Bulk CSV + AI Address Auditor, Scheduled Sends, Corporate Seal, and the Sentiment Dashboard.</div>
          <button onClick={() => setUpgradeGate(true)} data-testid="business-upsell-cta" className="text-xs flex items-center gap-1.5 bg-gradient-to-r from-primary to-secondary text-white rounded-lg px-3 py-1.5 hover:opacity-90">Go Business</button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-24 text-muted-foreground">Loading…</div>
      ) : orgs.length === 0 ? (
        <div className="surface rounded-[2rem] p-16 text-center">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-primary/60" />
          <h3 className="font-serif text-2xl mb-2">Start an Organization</h3>
          <p className="text-muted-foreground mb-6">Companies & charitable orgs — import a roster and automate team thoughtfulness.</p>
          <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-secondary text-white px-6 py-3 rounded-2xl hover:opacity-90"><Plus className="w-4 h-4" /> New Organization</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="orgs-grid">
          {orgs.map((o) => (
            <button key={o.id} onClick={() => openOrg(o.id)} className="surface rounded-[1.5rem] p-6 text-left card-soft" data-testid="org-card">
              <Building2 className="w-6 h-6 text-primary mb-3" />
              <div className="font-serif text-xl">{o.name}</div>
              <div className="text-sm text-muted-foreground">{(o.roster || []).length} member(s) · Captain view</div>
            </button>
          ))}
        </div>
      )}

      {/* Create org drawer */}
      <SlideOver open={createOpen} onClose={() => setCreateOpen(false)} title="New Organization" subtitle="You'll be its Captain">
        <div className="space-y-4">
          <input data-testid="org-name" value={name} onChange={(e) => setName(e.target.value)}
            className="w-full bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="Organization name" />
          <button data-testid="org-create-submit" onClick={createOrg} disabled={busy}
            className="w-full bg-gradient-to-r from-primary to-secondary text-white rounded-2xl py-3.5 hover:opacity-90 disabled:opacity-60">{busy ? "Creating…" : "Create Organization"}</button>
        </div>
      </SlideOver>

      {/* Manage org drawer */}
      <SlideOver open={!!active} onClose={() => setActive(null)} title={active?.name || "Organization"} subtitle="Captain dashboard" width="max-w-2xl">
        {active && (
          <div className="space-y-7">
            {/* Roster */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium flex items-center gap-2"><Users className="w-4 h-4 text-primary" /> Roster ({active.roster.length})</h3>
                <div className="flex items-center gap-2">
                  {businessActive ? (
                    <>
                      <button data-testid="audit-addresses-btn" onClick={auditAddresses} disabled={auditing || !active.roster.length}
                        className="text-xs flex items-center gap-1.5 surface rounded-lg px-3 py-1.5 hover:border-primary/40 disabled:opacity-60">
                        {auditing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />} AI Address Auditor
                      </button>
                      <button data-testid="import-csv-btn" onClick={() => csvRef.current?.click()} className="text-xs flex items-center gap-1.5 surface rounded-lg px-3 py-1.5 hover:border-primary/40"><Upload className="w-3.5 h-3.5" /> Import CSV</button>
                      <input ref={csvRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onCSV} data-testid="csv-input" />
                    </>
                  ) : (
                    <span data-testid="roster-tools-hidden" className="text-xs text-muted-foreground flex items-center gap-1"><Lock className="w-3 h-3" /> Bulk CSV + AI Address Auditor — Business Team Plan</span>
                  )}
                  <DirectorDebugTag requiredTier="Business ($29/mo)" />
                </div>
              </div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="roster-list">
                {active.roster.length === 0 ? <p className="text-sm text-muted-foreground">No members yet. Import a CSV (name,email,birthday,start_date,address,city,state,zip).</p> :
                  active.roster.map((m) => {
                    const verdict = auditResults?.results?.find((r) => r.member_id === m.id);
                    return (
                      <div key={m.id} className="flex items-center justify-between text-sm surface rounded-lg px-3 py-2">
                        <span>{m.name} <span className="text-muted-foreground">· {m.email}</span></span>
                        <div className="flex items-center gap-2">
                          {verdict && (
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${VERDICT_PILL[verdict.verdict] || VERDICT_PILL.no_address}`} data-testid="address-verdict-pill">
                              {verdict.deliverable === null ? "no address" : verdict.deliverable ? "deliverable" : "undeliverable"}
                            </span>
                          )}
                          <button onClick={() => removeMember(m.id)} className="text-muted-foreground hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    );
                  })}
              </div>
              {auditResults && (
                <p className="text-xs text-muted-foreground mt-2" data-testid="audit-summary">Audited {auditResults.audited} · {auditResults.deliverable} deliverable (real USPS-grade check via Lob).</p>
              )}
            </div>

            {/* Team Milestone Calendar */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium flex items-center gap-2"><CalendarClock className="w-4 h-4 text-primary" /> Team Milestones</h3>
                <button data-testid="auto-curate-btn" onClick={autoCurate} disabled={busy} className="text-xs flex items-center gap-1.5 bg-gradient-to-r from-primary to-secondary text-white rounded-lg px-3 py-1.5 hover:opacity-90 disabled:opacity-60">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Auto-curate (14d)
                </button>
              </div>
              {milestones.length === 0 ? <p className="text-sm text-muted-foreground">No upcoming milestones in 60 days.</p> :
                <div className="space-y-1.5" data-testid="team-milestones">
                  {milestones.map((m, i) => (
                    <div key={i} className="flex items-center justify-between text-sm surface rounded-lg px-3 py-2">
                      <span>{m.member_name} · {m.occasion}{m.years ? ` (${m.years}y)` : ""}</span>
                      <span className="gold-text">{m.days_until}d</span>
                    </div>
                  ))}
                </div>}
            </div>

            {/* Scheduled Sends + Sentiment Dashboard (Business Team Plan) — 100% invisible to
                Boutique orgs, not just labeled. */}
            {businessActive ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> Scheduled Sends</h3>
                  <div className="flex items-center gap-2">
                    <button data-testid="open-dashboard-btn" onClick={openDashboard} className="text-xs flex items-center gap-1.5 surface rounded-lg px-3 py-1.5 text-primary hover:border-primary/40">
                      <LayoutDashboard className="w-3.5 h-3.5" /> Sentiment Dashboard
                    </button>
                    <DirectorDebugTag requiredTier="Business ($29/mo)" />
                  </div>
                </div>
                <div className="surface rounded-xl p-3 space-y-2 mb-2">
                  <div className="grid grid-cols-2 gap-2">
                    <select value={newSend.member_id} onChange={(e) => setNewSend({ ...newSend, member_id: e.target.value })} data-testid="schedule-member"
                      className="bg-input border border-border rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40">
                      {active.roster.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                    <input type="date" value={newSend.send_date} onChange={(e) => setNewSend({ ...newSend, send_date: e.target.value })} data-testid="schedule-date"
                      className="bg-input border border-border rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input value={newSend.occasion} onChange={(e) => setNewSend({ ...newSend, occasion: e.target.value })} data-testid="schedule-occasion"
                      className="bg-input border border-border rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="Occasion" />
                    <select value={newSend.delivery_method} onChange={(e) => setNewSend({ ...newSend, delivery_method: e.target.value })} data-testid="schedule-method"
                      className="bg-input border border-border rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40">
                      <option value="physical">Physical</option>
                      <option value="digital">Digital</option>
                    </select>
                  </div>
                  <button data-testid="schedule-send-btn" onClick={createScheduledSend} disabled={schedulingBusy}
                    className="w-full text-sm flex items-center justify-center gap-1.5 bg-gradient-to-r from-secondary to-primary text-white rounded-lg py-2 hover:opacity-90 disabled:opacity-60">
                    {schedulingBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />} Schedule Send
                  </button>
                </div>
                {scheduled.length > 0 && (
                  <div className="space-y-1.5" data-testid="scheduled-sends-list">
                    {scheduled.map((s) => (
                      <div key={s.id} className="flex items-center justify-between text-sm surface rounded-lg px-3 py-2">
                        <span>{s.member_name} · {s.occasion} · {s.send_date}</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${s.status === "sent" ? "bg-secondary/15 text-secondary border-secondary/30" : s.status === "failed" ? "bg-red-500/10 text-red-400 border-red-500/20" : "bg-primary/10 text-primary border-primary/20"}`}>{s.status}</span>
                          {s.status === "scheduled" && <button onClick={() => deleteScheduledSend(s.id)} className="text-muted-foreground hover:text-red-500"><X className="w-3.5 h-3.5" /></button>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            {/* Corporate seal — Visual Harmony (literal upload + AI Style). 100% invisible to
                Boutique orgs. */}
            {businessActive ? (
              <div>
                <h3 className="font-medium flex items-center gap-2 mb-2"><ImageIcon className="w-4 h-4 text-primary" /> Corporate Seal <DirectorDebugTag requiredTier="Business ($29/mo)" /></h3>
                <div className="flex items-center gap-3">
                  {active.corporate_seal_base64 ? <img src={`data:image/png;base64,${active.corporate_seal_base64}`} alt="" className="w-12 h-12 object-contain surface rounded-lg p-1" /> : <div className="text-sm text-muted-foreground">Using default Gold Seal</div>}
                  <button data-testid="upload-seal-btn" onClick={() => sealRef.current?.click()} className="text-xs flex items-center gap-1.5 surface rounded-lg px-3 py-1.5 hover:border-primary/40"><Upload className="w-3.5 h-3.5" /> Upload logo</button>
                  <input ref={sealRef} type="file" accept="image/*" className="hidden" onChange={onSeal} data-testid="seal-input" />
                  <button data-testid="stylize-seal-btn" onClick={stylizeSeal} disabled={stylizing || !active.corporate_seal_base64}
                    className="text-xs flex items-center gap-1.5 bg-gradient-to-r from-[#c9a24a] to-primary text-white rounded-lg px-3 py-1.5 hover:opacity-90 disabled:opacity-60">
                    {stylizing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI Style — gold foil
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground/70 mt-1">Literal: your exact logo replaces the Gold Seal. AI Style: refines it into an embossed gold-foil finish (same shape, luxury texture).</p>
              </div>
            ) : null}

            {/* Social Fund / Group Pot */}
            <div className="surface rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium flex items-center gap-2"><Coins className="w-4 h-4 gold-text" /> Social Fund (Group Pot)</h3>
                <span className="text-sm">Balance: <span className="gold-text font-medium" data-testid="pot-balance">${fund?.pot_balance?.toFixed(2) ?? "0.00"}</span></span>
              </div>
              <div className="flex gap-2 mb-3">
                <input value={contrib.name} onChange={(e) => setContrib({ ...contrib, name: e.target.value })} data-testid="contrib-name"
                  className="flex-1 bg-input border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="Contributor" />
                <input type="number" value={contrib.amount} onChange={(e) => setContrib({ ...contrib, amount: e.target.value })} data-testid="contrib-amount"
                  className="w-24 bg-input border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="$" />
                <button data-testid="contribute-btn" onClick={contribute} className="surface rounded-xl px-4 text-primary text-sm hover:border-primary/40">Chip in</button>
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-1.5"><HeartHandshake className="w-3.5 h-3.5" /> Allocate to a charity:</div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {charities.slice(0, 4).map((c) => (
                  <button key={c.ein} onClick={() => allocateCharity(c.ein)} className="text-[11px] surface rounded-full px-2.5 py-1 hover:border-primary/40" data-testid="fund-charity">{c.name}</button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground/70 mt-2">DEMO pot — connect Stripe Connect for real collective funds.</p>
            </div>

            <a href="/impact" className="w-full flex items-center justify-center gap-2 surface rounded-2xl py-3 text-primary hover:border-primary/40" data-testid="impact-link">
              <FileText className="w-4 h-4" /> View Annual Impact Report
            </a>
          </div>
        )}
      </SlideOver>

      {/* Sentiment Dashboard */}
      <SlideOver open={dashboardOpen} onClose={() => setDashboardOpen(false)} title="Sentiment Dashboard" subtitle={dashboard?.org_name} width="max-w-2xl">
        {dashboard && (
          <div className="space-y-6" data-testid="sentiment-dashboard">
            <div className="grid grid-cols-3 gap-3">
              <div className="surface rounded-xl p-3 text-center"><div className="font-serif text-2xl gold-text">{dashboard.roster_size}</div><div className="text-xs text-muted-foreground">Roster</div></div>
              <div className="surface rounded-xl p-3 text-center"><div className="font-serif text-2xl gold-text">{dashboard.upcoming_count}</div><div className="text-xs text-muted-foreground">Upcoming Sends</div></div>
              <div className="surface rounded-xl p-3 text-center"><div className="font-serif text-2xl gold-text">{dashboard.sent_count}</div><div className="text-xs text-muted-foreground">Sent</div></div>
            </div>
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-primary" /> Calendar of Scheduled Sends</h4>
              {dashboard.scheduled_sends.length === 0 ? <p className="text-sm text-muted-foreground">Nothing scheduled yet.</p> : (
                <div className="space-y-1.5" data-testid="dashboard-calendar">
                  {dashboard.scheduled_sends.map((s) => (
                    <div key={s.id} className="flex items-center justify-between text-sm surface rounded-lg px-3 py-2">
                      <span className="gold-text font-medium">{s.send_date}</span>
                      <span>{s.member_name} · {s.occasion}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${s.status === "sent" ? "bg-secondary/15 text-secondary border-secondary/30" : s.status === "failed" ? "bg-red-500/10 text-red-400 border-red-500/20" : "bg-primary/10 text-primary border-primary/20"}`}>{s.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2"><Check className="w-4 h-4 text-primary" /> Delivery Status</h4>
              {dashboard.cards.length === 0 ? <p className="text-sm text-muted-foreground">No cards sent for this org yet.</p> : (
                <div className="space-y-1.5" data-testid="dashboard-cards">
                  {dashboard.cards.map((c) => (
                    <div key={c.id} className="flex items-center justify-between text-sm surface rounded-lg px-3 py-2">
                      <span>{c.recipient} · {c.occasion}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/50 border border-border">{c.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Celestial Sync — a natural, recurring reason to send a fresh batch every month:
                which zodiac season is live right now, and upcoming Gold Seal-worthy roster
                birthdays with their sign, so you know exactly which Celestial Insight card to
                send before the date hits. */}
            <div data-testid="celestial-sync">
              <h4 className="font-medium mb-2 flex items-center gap-2"><Moon className="w-4 h-4 text-primary" /> Celestial Sync</h4>
              {dashboard.celestial_sync?.current_season && (
                <div className="surface rounded-xl p-3 mb-2 flex items-center justify-between text-sm" data-testid="celestial-current-season">
                  <span className="flex items-center gap-2">
                    <span className="text-lg">{dashboard.celestial_sync.current_season.symbol}</span>
                    <span className="gold-text font-medium">{dashboard.celestial_sync.current_season.label} is live</span>
                  </span>
                  {dashboard.celestial_sync.next_season && (
                    <span className="text-xs text-muted-foreground">
                      {dashboard.celestial_sync.next_season.sign} Season in {dashboard.celestial_sync.next_season.starts_in_days}d
                    </span>
                  )}
                </div>
              )}
              {(dashboard.celestial_calendar || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No upcoming birthdays in the next 60 days.</p>
              ) : (
                <div className="space-y-1.5" data-testid="celestial-calendar">
                  {dashboard.celestial_calendar.map((m) => (
                    <div key={m.member_id} className="flex items-center justify-between text-sm surface rounded-lg px-3 py-2">
                      <span className="flex items-center gap-1.5">{m.zodiac?.symbol} {m.name}</span>
                      <span className="text-xs text-muted-foreground">{m.zodiac?.sign} · in {m.days_until}d</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Transaction Ledger — Directorate visibility over cash flow: gift amount,
                Maison fee, and REAL Stripe status for every send that carried a cash gift.
                stripe_live is only ever true for a genuine sk_live_ key — never faked. */}
            <div data-testid="transaction-ledger">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium flex items-center gap-2"><Coins className="w-4 h-4 text-primary" /> Transaction Ledger</h4>
                <span data-testid="stripe-live-indicator" className={`text-[10px] px-2 py-0.5 rounded-full border ${dashboard.stripe_live_key_active ? "bg-secondary/15 text-secondary border-secondary/30" : "bg-yellow-500/10 gold-text border-yellow-500/20"}`}>
                  {dashboard.stripe_live_key_active ? "Stripe LIVE key active" : "No real Stripe key — demo/test mode"}
                </span>
              </div>
              {(dashboard.transaction_ledger || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No cash gifts sent from this org yet.</p>
              ) : (
                <div className="space-y-1.5" data-testid="transaction-ledger-list">
                  {dashboard.transaction_ledger.map((t) => (
                    <div key={t.card_id} className="flex items-center justify-between text-sm surface rounded-lg px-3 py-2" data-testid="transaction-ledger-item">
                      <span>{t.recipient} · {t.occasion}</span>
                      <span className="flex items-center gap-2 text-xs">
                        <span className="gold-text font-medium">${Number(t.amount || 0).toFixed(2)}</span>
                        <span className="text-muted-foreground">+${Number(t.fee || 0).toFixed(2)} fee</span>
                        <span className={`px-2 py-0.5 rounded-full border ${t.status === "funded" ? "bg-secondary/15 text-secondary border-secondary/30" : "bg-yellow-500/10 gold-text border-yellow-500/20"}`}>
                          {t.status === "funded" ? "Funded (live)" : t.status || "Pending"}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SlideOver>

      <UpgradeModal open={upgradeGate} onClose={() => setUpgradeGate(false)} tier="business_team" feature="This Business feature" />
    </AppShell>
  );
};

export default Captain;
